import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AppInput, AppRemixIcon, AppSingleSelect, AppDatePicker } from '@/layout/AppComponents';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import ReactDOMServer from 'react-dom/server';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';
import Translator from '../../../../components/helpers/Translator';
import { axiosPath, path } from '../../../../../routing/helpers/router';
import { getUserManagementUserAccessCodeFilterInitialValues } from '../../../../../minions/forms';
import AuxSidebar from '../../../../Sidebar/AuxSidebar';
import useIsMounted from '../../../../components/hooks/useIsMounted';

export default function UserAccessCodeFilterSidebarBlock() {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { moduleBridgeHex, filtersHex } = useParams();
  const locale = useSelector((state) => state.global?.urlData?.lang);
  const auxSidebar = useSelector((state) => state.currentPage?.auxSidebar?.opened);
  const filters = useSelector((state) => state.currentPage?.userAccessCodeFilters);
  const [openOnce, setOpenOnce] = useState(true);
  const [noYesOptions] = useState([
    {
      label: <Translator file="user" name="general.no" />,
      value: 0,
    },
    {
      label: <Translator file="user" name="general.yes" />,
      value: 1,
    },
  ]);

  const handleAuxSidebar = () => {
    if (!openOnce) setOpenOnce(true);
    if (auxSidebar !== 'filters') {
      dispatch(currentPageAction.change('auxSidebar', { opened: 'filters' }));
    } else {
      dispatch(currentPageAction.change('auxSidebar', { opened: null }));
    }
  };

  const goToUserAccessCode = (hexCode) =>
    navigate(
      path({
        name: 'user_module_userManagement_userAccessCode',
        params: {
          moduleBridgeHex,
          page: 1,
          filtersHex: hexCode,
        },
      }),
    );

  // > GENERATE FILTER HEX CODE
  const generateFilterHexCode = async (values, setSubmitting) =>
    axios
      .post(
        axiosPath(
          'api_r_user_module_userManagement_userAccessCode_generateFilterHexCode',
        ),
        { moduleBridgeHex, filters: values },
      )
      .then(({ data }) => {
        setSubmitting(false);
        goToUserAccessCode(data.hexCode);
        dispatch(currentPageAction.change('auxSidebar', { opened: null }));
      });

  const handleOnSubmit = (values, { setSubmitting }) => {
    generateFilterHexCode(values, setSubmitting);
  };
  // < GENERATE FILTER HEX CODE

  // > FILTERS
  const getFilters = useCallback(
    async () =>
      axios
        .post(
          axiosPath('api_r_user_module_userManagement_userAccessCode_getFilters'),
          {
            moduleBridgeHex,
            filtersHex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change('userAccessCodeFilters', {
                ...getUserManagementUserAccessCodeFilterInitialValues(),
                ...data,
              }),
            );
          }
        }),
    [moduleBridgeHex, filtersHex],
  );

  useEffect(() => {
    if (filtersHex !== '0') {
      getFilters();
    } else {
      dispatch(
        currentPageAction.change(
          'userAccessCodeFilters',
          getUserManagementUserAccessCodeFilterInitialValues(),
          true,
        ),
      );
    }
  }, [filtersHex]);
  // < FILTERS

  return (
    <>
      <li
        className={auxSidebar && auxSidebar === 'filters' ? 'active' : ''}
        onClick={() => handleAuxSidebar()}
      >
        <span className="sidebar-element">
          <AppRemixIcon icon="equalizer" className="sidebar-icon" />{' '}
          <span className="sb-title">
            <Translator file="module-legalCompliance" name="filtering" />
          </span>
        </span>
      </li>
      <AuxSidebar
        title={<Translator file="module-legalCompliance" name="filtering" />}
        isOpen={auxSidebar && auxSidebar === 'filters'}
        width="350px"
        position="right"
        onClose={handleAuxSidebar}
      >
        {openOnce && (
          <>
            {filters && (
              <Formik
                validateOnChange={false}
                validateOnBlur={false}
                enableReinitialize
                initialValues={filters}
                onSubmit={handleOnSubmit}
              >
                {({ setFieldValue, handleSubmit, values, errors, isSubmitting }) => (
                  <>
                    <div className="d-flex mt-3 mb-3">
                      <span
                        className="btn btn-info btn-block"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                      >
                        <Translator file="module-legalCompliance" name="filtering" />
                      </span>
                      {filtersHex !== '0' && (
                        <span
                          title={ReactDOMServer.renderToString(
                            <Translator
                              file="module-legalCompliance"
                              name="delete.filters"
                            />,
                          )}
                          className="btn btn-outline-danger ml-1"
                          onClick={() =>
                            navigate(
                              path({
                                name: 'user_module_userManagement_userAccessCode',
                                params: {
                                  moduleBridgeHex,
                                  page: 1,
                                  filtersHex: 0,
                                },
                              }),
                            )
                          }
                        >
                          <AppRemixIcon icon="delete-bin-6" />
                        </span>
                      )}
                    </div>
                    <AppInput
                      title="Código"
                      icon={<AppRemixIcon icon="fingerprint" />}
                      value={values.code}
                      onChange={(opt) => setFieldValue('code', opt)}
                      isClearable
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha añadido desde"
                      value={values.dateFrom}
                      onChange={(opt) => setFieldValue('dateFrom', opt)}
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha añadido hasta"
                      value={values.dateTo}
                      onChange={(opt) => setFieldValue('dateTo', opt)}
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha registrado desde"
                      value={values.createdDateFrom}
                      onChange={(opt) => setFieldValue('createdDateFrom', opt)}
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppDatePicker
                      locale={locale}
                      title="Fecha registrado hasta"
                      value={values.createdDateTo}
                      onChange={(opt) => setFieldValue('createdDateTo', opt)}
                      showYearDropdown
                      showMonthDropdown
                    />
                    <AppSingleSelect
                      options={noYesOptions}
                      title="¿Ha sido creado?"
                      value={values?.isCreated}
                      error={errors?.isCreated}
                      onChange={(opt) => {
                        setFieldValue('isCreated', opt);
                      }}
                      isClearable
                    />
                  </>
                )}
              </Formik>
            )}
          </>
        )}
      </AuxSidebar>
    </>
  );
}
